/**
 * Vuex Module: Filtros de Ocupação
 * 
 * Gerencia os filtros do dashboard de ocupação com sincronização
 * bidirecional com a URL (query params).
 * 
 * Uso:
 * - store.dispatch('roomAllocationFilters/updateFilters', { brand_id: 1 })
 * - store.dispatch('roomAllocationFilters/syncFromRoute', route.query)
 * - store.getters['roomAllocationFilters/queryParams']
 */

const FILTER_KEYS = [
    'brand_id',
    'campus_id',
    'campus_parent_id',
    'shift_id',
    'course_id',
    'work_time_id',
    'ambience_type_id',
    'search'
]

const state = () => ({
    brand_id: null,
    campus_id: null,
    campus_parent_id: null,
    shift_id: null,
    course_id: null,
    work_time_id: null,
    ambience_type_id: null,
    search: '',
    
    // Estado de UI relacionado a filtros
    isFilterPanelOpen: false,
    lastSyncedAt: null
})

const getters = {
    /**
     * Retorna todos os filtros ativos (não nulos)
     */
    activeFilters: (state) => {
        return FILTER_KEYS.reduce((acc, key) => {
            if (state[key] !== null && state[key] !== '' && state[key] !== undefined) {
                acc[key] = state[key]
            }
            return acc
        }, {})
    },

    /**
     * Retorna os filtros formatados para query params da URL
     */
    queryParams: (state) => {
        const params = {}
        FILTER_KEYS.forEach(key => {
            if (state[key] !== null && state[key] !== '' && state[key] !== undefined) {
                params[key] = state[key]
            }
        })
        return params
    },

    /**
     * Verifica se há filtros ativos
     */
    hasActiveFilters: (state, getters) => {
        return Object.keys(getters.activeFilters).length > 0
    },

    /**
     * Conta quantos filtros estão ativos
     */
    activeFiltersCount: (state, getters) => {
        return Object.keys(getters.activeFilters).length
    }
}

const mutations = {
    SET_FILTER(state, { key, value }) {
        if (FILTER_KEYS.includes(key)) {
            state[key] = value
        }
    },

    SET_FILTERS(state, filters) {
        FILTER_KEYS.forEach(key => {
            if (filters.hasOwnProperty(key)) {
                state[key] = filters[key]
            }
        })
        state.lastSyncedAt = new Date().toISOString()
    },

    CLEAR_FILTERS(state) {
        FILTER_KEYS.forEach(key => {
            state[key] = key === 'search' ? '' : null
        })
    },

    SET_FILTER_PANEL_OPEN(state, isOpen) {
        state.isFilterPanelOpen = isOpen
    }
}

const actions = {
    /**
     * Atualiza um único filtro
     */
    updateFilter({ commit, dispatch }, { key, value }) {
        commit('SET_FILTER', { key, value })
        dispatch('notifyChange')
    },

    /**
     * Atualiza múltiplos filtros de uma vez
     */
    updateFilters({ commit, dispatch }, filters) {
        commit('SET_FILTERS', filters)
        dispatch('notifyChange')
    },

    /**
     * Sincroniza os filtros a partir dos query params da rota
     */
    syncFromRoute({ commit }, query) {
        const filters = {}
        FILTER_KEYS.forEach(key => {
            if (query[key] !== undefined) {
                // Converte strings numéricas para números
                const value = query[key]
                if (value === 'null' || value === '') {
                    filters[key] = null
                } else if (!Number.isNaN(Number(value)) && value !== '') {
                    filters[key] = Number.parseInt(value, 10)
                } else {
                    filters[key] = value
                }
            }
        })
        
        if (Object.keys(filters).length > 0) {
            commit('SET_FILTERS', filters)
        }
    },

    /**
     * Limpa todos os filtros
     */
    clearFilters({ commit, dispatch }) {
        commit('CLEAR_FILTERS')
        dispatch('notifyChange')
    },

    /**
     * Notifica que os filtros mudaram (para sincronização com URL)
     */
    notifyChange({ getters }) {
        // Este evento pode ser usado pelo componente para sincronizar com a URL
        if (typeof globalThis !== 'undefined' && globalThis.window) {
            globalThis.dispatchEvent(new CustomEvent('room-allocation-filters-changed', {
                detail: getters.queryParams
            }))
        }
    },

    /**
     * Toggle do painel de filtros
     */
    toggleFilterPanel({ commit, state }) {
        commit('SET_FILTER_PANEL_OPEN', !state.isFilterPanelOpen)
    }
}

export const store = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}

export default store
